import {
	deleteDraftModalDefaultValues,
	MANAGE_MIPS_FORM_FIELDS,
	OPTIONS,
	useListMip,
} from '@dealstack/frontend/admin/pages/Manage/MIP/ListMip/useListMip';
import { ManageMipConfirmationModal } from '@dealstack/frontend/admin/pages/Manage/MIP/ManageMip/ManageMipConfirmationModal/ManageMipConfirmationModal';
import { Option, Select } from '@dealstack/frontend/shared/components/forms/Select/Select';
import { StringInput } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { DataMipTct } from '@dealstack/shared/lib/generated';
import { EMPTY_CALLBACK } from '@dealstack/shared/lib/utils';
import { flexRender, Row } from '@tanstack/react-table';
import { memo } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

interface Props {
	className?: string;
}

const ListMip = ({ className }: Props) => {
	const {
		tableContainerRef,
		getMIPRequest,
		rows,
		tanstackTable,
		rowVirtualizer,
		manageSearchQueryForm,
		manageSelectForm,
		deleteDraftMipConfirmationModal,
		setDeleteDraftMipConfirmationModal,
		deleteDraftIPOnSubmit,
		isDeletingDraftIp,
	} = useListMip();

	const totalSize = rowVirtualizer.getTotalSize();
	const virtualRows = rowVirtualizer.getVirtualItems();

	const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
	const paddingBottom =
		virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

	return (
		<div className={className}>
			<FormsWrapper>
				<FormProvider {...manageSearchQueryForm}>
					<form onSubmit={manageSearchQueryForm.handleSubmit(EMPTY_CALLBACK)}>
						<SearchBox>
							<StringInput
								label="Search query"
								name={MANAGE_MIPS_FORM_FIELDS.SEARCH_QUERY}
							/>
						</SearchBox>
					</form>
				</FormProvider>
				<FormProvider {...manageSelectForm}>
					<SearchBox>
						<Select label="Hide archived" name="showArchived">
							<Option key={OPTIONS.TRUE} value={OPTIONS.TRUE}>
								No
							</Option>
							<Option key={OPTIONS.FALSE} value={OPTIONS.FALSE}>
								Yes
							</Option>
						</Select>
					</SearchBox>
					<SearchBox>
						<Select label="Hide draft" name="showDraft">
							<Option key={OPTIONS.TRUE} value={OPTIONS.TRUE}>
								No
							</Option>
							<Option key={OPTIONS.FALSE} value={OPTIONS.FALSE}>
								Yes
							</Option>
						</Select>
					</SearchBox>
				</FormProvider>
			</FormsWrapper>
			<TableWrapper ref={tableContainerRef}>
				{getMIPRequest?.isFetching ? <InlineLoader /> : null}
				<table className="table">
					<thead>
						{tanstackTable.getHeaderGroups().map((headerGroup) => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<th
											key={header.id}
											colSpan={header.colSpan}
											style={{ width: header.getSize() }}
										>
											{header.isPlaceholder ? null : (
												<div
													{...{
														className: header.column.getCanSort()
															? 'cursor-pointer clickAnimation select-none'
															: '',
														onClick:
															header.column.getToggleSortingHandler(),
													}}
												>
													{flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
													{{
														asc: ' 🔼',
														desc: ' 🔽',
													}[header.column.getIsSorted() as string] ??
														null}
												</div>
											)}
										</th>
									);
								})}
							</tr>
						))}
					</thead>
					<tbody>
						{paddingTop > 0 && (
							<tr>
								<td style={{ height: `${paddingTop}px` }} />
							</tr>
						)}
						{rowVirtualizer.getVirtualItems().map((virtualRow) => {
							const row = rows[virtualRow.index] as Row<DataMipTct>;
							return (
								<tr key={row.id}>
									{row.getVisibleCells().map((cell) => {
										return (
											<td key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</td>
										);
									})}
								</tr>
							);
						})}
						{paddingBottom > 0 && (
							<tr>
								<td style={{ height: `${paddingBottom}px` }} />
							</tr>
						)}
					</tbody>
				</table>
			</TableWrapper>
			<br />
			<div>{tanstackTable.getRowModel().rows.length} Rows</div>
			{deleteDraftMipConfirmationModal.visible && (
				<ManageMipConfirmationModal
					className={className}
					onClose={() =>
						setDeleteDraftMipConfirmationModal(deleteDraftModalDefaultValues)
					}
					onSubmit={() => deleteDraftIPOnSubmit(deleteDraftMipConfirmationModal.mipId)}
					isInProgress={isDeletingDraftIp}
					content={{
						info: `You're on your way to delete the draft IP.`,
						warning: 'Be warned that you could not undo this action',
						buttonText: 'Delete IP ',
					}}
					secondaryButton={
						<Button
							onClick={() =>
								deleteDraftIPOnSubmit(deleteDraftMipConfirmationModal.mipId, true)
							}
							iconPlacement="left"
							variant="delete"
						>
							Delete IP with entity and securities
						</Button>
					}
				/>
			)}
		</div>
	);
};

const TableWrapper = styled.div`
	position: relative;
	border: 1px solid lightgray;
	height: 40rem;
	overflow: auto;

	table {
		border-collapse: collapse;
		border-spacing: 0;
		font-family: arial, sans-serif;
		table-layout: fixed;
		width: 100%;
	}

	thead {
		background: white !important;
		margin: 0;
		position: sticky;
		top: 0;
		opacity: 1;
		z-index: 1;
	}

	th {
		border-bottom: 1px solid lightgray;
		border-right: 1px solid lightgray;
		padding: 2px 4px;
		text-align: left;
	}

	td {
		padding: 6px;
	}
`;
const FormsWrapper = styled.div`
	display: flex;
`;
const SearchBox = styled.div`
	display: flex;
	align-items: end;
`;

const Memoized = memo(ListMip);
const Styled = styled(Memoized)`
	${SearchBox} {
		margin-bottom: 2rem;
		& > * {
			margin-right: 2rem;
		}
	}
`;

export { Styled as ListMip };
